/**
 * NewProspect (TASK-659) — the generative prospect-intake widget.
 *
 * "Onboard a new prospective client" summons THIS instead of a prose interview:
 * a progressive two-step card (essentials first, optional detail second) with
 * duplicate detection before creation. On success the SAME canvas continues
 * into the resumable OnboardingChecklist — no second, disconnected experience.
 *
 * Creates a PROSPECT (clients.status='prospect') via the existing client-create
 * service; conversion to client stays behind the mandatory gates
 * (POST /clients/{id}/convert — TASK-659 backend).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { clientService } from '../../../services/clientService';
import { useCanvasStore } from '../../../stores/canvasStore';
import { StatusBadge } from '../../ui/status-badge';
import { WidgetContainer } from '../WidgetContainer';

const ENTITY_TYPES = ['individual', 'corporation', 'foundation', 'trust'] as const;

export function NewProspect() {
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  const openClient = useCanvasStore((s) => s.openClient);

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>('individual');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jurisdiction, setJurisdiction] = useState('CH');
  const [creating, setCreating] = useState(false);
  const [dupOverride, setDupOverride] = useState(false);

  // Roster for duplicate detection (name/email) before anything is created.
  const roster = useQuery({
    queryKey: ['clients-roster-dup-check'],
    queryFn: () => clientService.fetchClients(0, 200),
    // Duplicate protection must see records created seconds ago.
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const duplicates = (roster.data?.clients ?? []).filter((c) => {
    const nameHit =
      name.trim().length > 3 && c.name?.trim().toLowerCase() === name.trim().toLowerCase();
    const emailHit =
      email.trim().length > 3 && c.email?.trim().toLowerCase() === email.trim().toLowerCase();
    return nameHit || emailHit;
  });

  const essentialsValid = name.trim().length > 1;
  const detailsValid = /.+@.+\..+/.test(email.trim());

  const create = async () => {
    setCreating(true);
    try {
      const created = await clientService.createClient({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        type: entityType,
        jurisdiction: jurisdiction.trim() || undefined,
        is_prospect: true,
      });
      toast.success(`Prospect ${created.name} created — continuing to the onboarding checklist`);
      // The same canvas continues into the resumable checklist.
      addSpecs([
        {
          component: 'OnboardingChecklist',
          props: { clientId: created.id, clientName: created.name },
        },
      ]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create the prospect';
      toast.error(/exists/i.test(message) ? 'A client with this email already exists' : message);
    } finally {
      setCreating(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary';

  return (
    <WidgetContainer title="New prospect" source="Client records">
      <div data-testid="new-prospect-widget" className="max-w-md space-y-3">
        <p className="text-[13px] text-muted-foreground">
          Creates a <strong>prospect</strong> — a real record with limited actions until KYC
          and the risk profile pass their gates. Step {step} of 2.
        </p>

        {step === 1 && (
          <>
            <label className="block text-xs font-medium text-muted-foreground" htmlFor="np-name">
              Full name / entity name
            </label>
            <input
              id="np-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDupOverride(false);
              }}
              placeholder="Client full name"
              className={inputClass}
            />
            <label className="block text-xs font-medium text-muted-foreground" htmlFor="np-type">
              Entity type
            </label>
            <select
              id="np-type"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as (typeof ENTITY_TYPES)[number])}
              className={inputClass}
            >
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t[0].toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!essentialsValid}
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm text-foreground">
              {name} <StatusBadge status="attention" size="sm">Prospect</StatusBadge>
            </p>
            <label className="block text-xs font-medium text-muted-foreground" htmlFor="np-email">
              Email (used for duplicate protection)
            </label>
            <input
              id="np-email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setDupOverride(false);
              }}
              placeholder="client@example.ch"
              className={inputClass}
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-muted-foreground" htmlFor="np-phone">
                  Phone (optional)
                </label>
                <input id="np-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label
                  className="block text-xs font-medium text-muted-foreground"
                  htmlFor="np-jurisdiction"
                >
                  Jurisdiction
                </label>
                <input
                  id="np-jurisdiction"
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Duplicate detection BEFORE creation. */}
            {duplicates.length > 0 && !dupOverride && (
              <div
                data-testid="duplicate-warning"
                className="rounded-lg border border-status-attention/30 bg-status-attention/10 p-3 text-[13px] text-status-attention-fg"
              >
                <p className="flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4" /> Possible existing record
                </p>
                <ul className="mt-1 space-y-1">
                  {duplicates.slice(0, 3).map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-2">
                      <span className="truncate">
                        {d.name} · {d.email}
                      </span>
                      <button
                        type="button"
                        onClick={() => openClient(d.id)}
                        className="shrink-0 text-primary underline"
                      >
                        Open existing
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setDupOverride(true)}
                  className="mt-2 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground"
                >
                  Create anyway — this is a different person
                </button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
              >
                Back
              </button>
              <button
                type="button"
                disabled={creating || !detailsValid || (duplicates.length > 0 && !dupOverride)}
                onClick={create}
                data-testid="create-prospect"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                <UserPlus className="h-4 w-4" />
                {creating ? 'Creating…' : 'Create prospect'}
              </button>
            </div>
          </>
        )}
      </div>
    </WidgetContainer>
  );
}
